import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, OFFICE_ADDRESS } from "@/lib/contact";

const SECTIONS = [
  {
    title: "1. Who We Are",
    body: "EONFAIRFOX.IN is an independent marketing and investment advisory website operated by Homewiz.in (\"we\", \"us\", \"our\"). It is not the official website of Fairfox IT Infra Pvt. Ltd. This policy explains how we handle information shared with us through this website.",
  },
  {
    title: "2. Information We Collect",
    body: "When you submit an enquiry or site-visit request, we collect the details you provide: your name, mobile number, budget range, property preferences (primary/resale, office/retail, purpose) and preferred visit date. We also receive the content of messages you send us on WhatsApp, email or phone. Like most websites, we collect standard analytics data (pages viewed, scroll depth, button interactions) via Google Tag Manager.",
  },
  {
    title: "3. How We Use Your Information",
    body: "We use your details solely to respond to your enquiry: sharing current primary and resale options, arranging site visits, coordinating fit-out or leasing assistance, and following up on your investment interest. We do not sell your personal information to third parties.",
  },
  {
    title: "4. WhatsApp, Email & Call Communication",
    body: "By tapping a WhatsApp button or submitting the enquiry form, you consent to being contacted by our investment desk on WhatsApp, email or phone regarding EON Fairfox and comparable opportunities. You can ask us to stop at any time by replying \"STOP\" on WhatsApp or emailing us.",
  },
  {
    title: "5. Cookies & Analytics",
    body: "We use Google Tag Manager (which may load Google Analytics and Google Ads tags) to measure advertising performance and site engagement. These tools may set cookies or use device identifiers. You can control cookies through your browser settings.",
  },
  {
    title: "6. Sharing of Information",
    body: "Your enquiry details stay with the Homewiz.in investment desk. Where your enquiry concerns a developer's primary inventory, relevant details may be shared with the developer's sales team only to progress your purchase or site visit.",
  },
  {
    title: "7. Data Retention & Security",
    body: "Enquiry records are retained while your requirement is active and for a reasonable period afterwards for follow-up and record-keeping. We apply reasonable technical and organisational measures to protect your information.",
  },
  {
    title: "8. Your Rights",
    body: `You may request access, correction or deletion of the personal information we hold about you at any time by emailing ${CONTACT_EMAIL} or calling ${CONTACT_PHONE_DISPLAY}.`,
  },
  {
    title: "9. Changes to This Policy",
    body: "We may update this policy from time to time. The latest version will always be available at this URL. Last updated: October 2026.",
  },
  {
    title: "10. Contact",
    body: `Homewiz.in — Independent Marketing & Investment Advisory. Email: ${CONTACT_EMAIL} · Phone / WhatsApp: ${CONTACT_PHONE_DISPLAY} · Our investment desk: ${OFFICE_ADDRESS.full}.`,
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[#F3F4F6] font-body antialiased">
      <header className="border-b border-white/[0.07] bg-[#0B0C0E]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#9CA3AF] hover:text-[#D4AF37] transition-colors duration-300"
          >
            ← Back to Site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p data-testid="privacy-operator-disclosure" className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Homewiz.in · Independent EON Fairfox Investment Desk</p>
          <h1 data-testid="privacy-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            Privacy <span className="gold-text italic">Policy.</span>
          </h1>
          <p className="mt-4 text-sm text-[#9CA3AF]">Last updated: October 2026</p>
        </motion.div>

        <div className="mt-12 space-y-8">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              data-testid={`privacy-section-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-white/[0.07] bg-[#121418] p-7"
            >
              <h2 className="font-display text-xl font-semibold text-[#F3E5AB]">{s.title}</h2>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{s.body}</p>
            </motion.section>
          ))}
        </div>
      </main>
    </div>
  );
}
